import { useCallback } from 'react'
import { GridApi } from 'ag-grid-community'
import { CalendarDateVO } from '../../../../../../../../../../domain/value-object/CalendarDateVO'
import objects from '../../../../../../../../../../utils/objects'
import { useDependencyContext } from './DependencyContext'
import {
  collectCascadedRows,
  countWorkdayDelta,
} from './cascadeDependencySlide'

type ScheduledDate = {
  startDate: Date | null
  endDate: Date | null
}

type UseCascadeSlideParams = {
  api: GridApi
  timeScale: CalendarDateVO[]
  dataPath: string
}

/**
 * 依存関係の連動スライド（カスケード）処理を提供するフック。
 * Ctrl+ドラッグ時にのみ呼び出される想定。
 */
export const useDependencyCascadeSlide = ({
  api,
  timeScale,
  dataPath,
}: UseCascadeSlideParams) => {
  const dependencyContext = useDependencyContext()
  const prefix = dataPath ? dataPath + '.' : ''

  const cascade = useCallback(
    (
      updatedRows: Record<string, unknown>[],
      oldDate: ScheduledDate,
      newDate: ScheduledDate
    ): Record<string, unknown>[] => {
      if (!dependencyContext) return []

      const allMovedUuids = new Set(
        updatedRows
          .map(
            (r: Record<string, unknown>) =>
              objects.getValue(r, `${prefix}wbsItem`)?.uuid as
                | string
                | undefined
          )
          .filter((uuid): uuid is string => !!uuid)
      )

      const commonParams = {
        sourceWbsItemUuids: allMovedUuids,
        dependencies: dependencyContext.dependencies,
        wbsItemUuidToRowIndexMap:
          dependencyContext.getWbsItemUuidToRowIndexMap(),
        api,
        scale: timeScale,
        dataPath,
      }

      const cascadedMap = new Map<string, Record<string, unknown>>()

      // 前方向カスケード: 始点のendDate変動 → 後続をスライド
      if (oldDate.endDate && newDate.endDate) {
        const endDelta = countWorkdayDelta(
          timeScale,
          CalendarDateVO.of(oldDate.endDate),
          CalendarDateVO.of(newDate.endDate)
        )
        if (endDelta !== 0) {
          const rows = collectCascadedRows({
            ...commonParams,
            workdayDelta: endDelta,
            direction: 'forward',
          })
          for (const row of rows) {
            const uuid = objects.getValue(row, `${prefix}wbsItem`)?.uuid
            if (uuid && !cascadedMap.has(uuid)) {
              cascadedMap.set(uuid, row)
            }
          }
        }
      }

      // 後方向カスケード: 終点のstartDate変動 → 先行をスライド
      if (oldDate.startDate && newDate.startDate) {
        const startDelta = countWorkdayDelta(
          timeScale,
          CalendarDateVO.of(oldDate.startDate),
          CalendarDateVO.of(newDate.startDate)
        )
        if (startDelta !== 0) {
          const rows = collectCascadedRows({
            ...commonParams,
            workdayDelta: startDelta,
            direction: 'backward',
          })
          for (const row of rows) {
            const uuid = objects.getValue(row, `${prefix}wbsItem`)?.uuid
            if (uuid && !cascadedMap.has(uuid)) {
              cascadedMap.set(uuid, row)
            }
          }
        }
      }

      return Array.from(cascadedMap.values())
    },
    [dependencyContext, api, timeScale, dataPath, prefix]
  )

  return { cascade }
}
